import Close from '@mui/icons-material/Close';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useTranslation } from 'react-i18next';
import { useIsTruncated } from 'hooks/useIsTruncated';
import { CrmLogos } from 'images/integrations/logosSvg';
import { useUnlinkCrmObject } from 'pages/flex-data/flex-data-links/useUnlinkCrmObject';
import type { FlexDataLink } from 'pages/flex-data/flex-data-links/types';

interface CrmObjectRowProps {
  obj: FlexDataLink;
  contentId: string;
  variant: 'staged' | 'linked';
  onRemoveStaged?: (objectId: string) => void;
}

export function CrmObjectRow({ obj, contentId, variant, onRemoveStaged }: CrmObjectRowProps) {
  const { t } = useTranslation();
  const { ref, isTruncated } = useIsTruncated();
  const { unlinkObject, isPending, isError: isUnlinkError } = useUnlinkCrmObject(contentId);

  const isLinked = variant === 'linked';
  const isDeleted = isLinked && obj.status === 'deleted';
  const isError = isLinked && (isUnlinkError || isDeleted);

  const handleRemove = () => {
    if (isLinked) {
      unlinkObject(obj);
    } else {
      onRemoveStaged?.(obj.objectId);
    }
  };

  const buttonLabel = isLinked
    ? t('editor.side_panel.link_crm_object_modal.unlink_from_document')
    : t('editor.side_panel.link_crm_object_modal.remove');

  return (
    <Stack direction="column" gap={0.375}>
      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        sx={{
          border: '1px solid',
          borderColor: isError ? 'error.main' : 'elevation.outlined',
          borderRadius: '8px',
          p: 1,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <Stack direction="row" alignItems="center" gap={1.25} sx={{ flex: 1, minWidth: 0 }}>
          <img
            src={CrmLogos[obj.dataSourceType.toLowerCase()]}
            alt={obj.dataSourceType}
            aria-label={obj.dataSourceType}
            style={{ width: 24, height: 24, objectFit: 'contain', flexShrink: 0 }}
          />
          <Tooltip title={obj.objectLabel} disableHoverListener={!isTruncated}>
            <Typography ref={ref} variant="subtitle2" noWrap>
              {obj.objectLabel}
            </Typography>
          </Tooltip>
        </Stack>
        {isLinked && isPending ? (
          <CircularProgress size={20} sx={{ flexShrink: 0 }} />
        ) : (
          <Tooltip title={buttonLabel}>
            <IconButton size="small" onClick={handleRemove} disabled={isPending} aria-label={buttonLabel}>
              <Close fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>
      {isUnlinkError && <FormHelperText error>{t('editor.side_panel.link_crm_object_modal.unlink_error')}</FormHelperText>}
      {isDeleted && !isUnlinkError && <FormHelperText error>{t('editor.side_panel.link_crm_object_modal.object_deleted')}</FormHelperText>}
    </Stack>
  );
}
